/**
 * <product-card>
 *
 * Product card interactions: image slider, pressed state, card navigation,
 * size add-to-cart, and color swatch links.
 *
 * Loaded globally from layout/theme.liquid.
 *
 * Public contract:
 *   Attributes: [data-url], [data-slider], [data-slide-count]
 *   Methods: setCardSize('big' | 'medium' | 'small')
 *   Actions: [data-action=pc-prev|pc-next|pc-add-size|pc-color]
 *   Events: product-card:navigate — bubbles before fallback JavaScript navigation to the PDP
 *   Classes: .product-card--pressed, .is-active on slides, .is-disabled on arrows
 *   Media: desktop 30px arrow controls; horizontal swipe on pocket.
 *   Small cards (collection grid 6-up desktop / 3-up mobile) get no arrows and no
 *   swipe on any breakpoint — desktop still previews the second image on hover.
 *   On pocket (mobile/tablet), slide changes use swipe only (arrows are desktop-hover).
 *   Medium cards on pocket press on HOLD or DRAG-OVER, not on tap (Figma 13052-116714 /
 *   13052-116748 "very sensitive, same as CDLP"): the card under the finger takes the
 *   pressed state — while scrolling the grid past it, swiping its images, or by resting
 *   on it — and reveals the sizes row via CSS. The state persists after the finger lifts
 *   and is transferred when another card is pressed. The click synthesized from the
 *   initiating hold is swallowed; a later deliberate tap still goes to the PDP.
 *   Inside <swiper-init> on pocket (mobile/tablet): image slider is disabled so the
 *   outer carousel can scroll, and the card never enters the pressed state
 *   (slider-section cards are 'big' per Figma). Desktop keeps the image slider
 *   (hover arrows).
 */

import { responsive } from '@theme/responsive';
import { addVariantToCart } from '@theme/cart-api';

const PRESSED_CLASS = 'product-card--pressed';
const NAVIGATING_CLASS = 'product-card--navigating';
const ACTIVE_CLASS = 'is-active';
const DISABLED_CLASS = 'is-disabled';
const SWIPE_THRESHOLD_PX = 20;
const AXIS_LOCK_PX = 8;
// Hold this long with a finger down and the card presses (revealing its sizes).
// Short enough that resting on a card mid-scroll counts as a hold, long enough
// that a deliberate tap goes to the PDP without the sizes flashing first.
const PRESS_HOLD_MS = 140;
// Past this much finger travel the gesture is a drag, not a tap, so the card under
// the finger presses immediately. Roughly the browser's own tap slop, so a real tap
// stays under it and never flashes the sizes on its way to the PDP.
const DRAG_PRESS_SLOP_PX = 10;

// Pocket cards navigate via data-url rather than the overlay link, so keep
// touch/hover prefetching independent of that desktop-only anchor.
const prefetchedUrls = new Set();
const canPrefetch = document.createElement('link').relList.supports('prefetch');

// Prefetched documents bake in the server-rendered cart count. After any cart
// mutation, forget what was prefetched so the next touch/hover fetches fresh
// HTML (the re-prefetch replaces the stale cache entry before the click).
document.addEventListener('product:added', () => prefetchedUrls.clear());
document.addEventListener('cart:refreshed', () => prefetchedUrls.clear());

function prefetchDocument(url) {
  if (!canPrefetch || !url || url === '#' || prefetchedUrls.has(url)) {
    return;
  }
  prefetchedUrls.add(url);
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = url;
  link.as = 'document';
  link.fetchPriority = 'high';
  document.head.append(link);
}

class ProductCard extends HTMLElement {
  static #breakpointsRegistered = false;

  // Drag-over pressing, one tracker for the whole page.
  //
  // A touch never retargets: every touchmove of a gesture is delivered to the element
  // the finger first landed on, so a card can't learn from its own listeners that the
  // finger has since drifted onto it. The card under the finger has to be hit-tested
  // from a single document-level tracker instead — that is what makes the pressed state
  // "very sensitive" the way CDLP's is (Figma 13052-116748).
  static #trackerBound = false;
  static #touchActive = false;
  static #touchDragging = false;
  static #touchStartX = 0;
  static #touchStartY = 0;
  static #touchX = 0;
  static #touchY = 0;
  static #hitTestFrame = 0;

  static #onTrackerTouchStart = (event) => {
    const touch = event.touches[0];
    // Second finger down: a pinch/zoom is not a drag over the grid.
    if (!touch || event.touches.length > 1) {
      ProductCard.#touchActive = false;
      return;
    }
    ProductCard.#touchActive = true;
    ProductCard.#touchDragging = false;
    ProductCard.#touchStartX = touch.clientX;
    ProductCard.#touchStartY = touch.clientY;
    ProductCard.#touchX = touch.clientX;
    ProductCard.#touchY = touch.clientY;
  };

  static #onTrackerTouchMove = (event) => {
    const touch = event.touches[0];
    if (!ProductCard.#touchActive || !touch) {
      return;
    }
    ProductCard.#touchX = touch.clientX;
    ProductCard.#touchY = touch.clientY;

    if (!ProductCard.#touchDragging) {
      const deltaX = Math.abs(touch.clientX - ProductCard.#touchStartX);
      const deltaY = Math.abs(touch.clientY - ProductCard.#touchStartY);
      if (Math.max(deltaX, deltaY) < DRAG_PRESS_SLOP_PX) {
        return;
      }
      ProductCard.#touchDragging = true;
    }

    ProductCard.#scheduleDragPress();
  };

  // The finger can hold still while the grid moves under it — that is the whole point of
  // a scroll — and touchmove is throttled (and on some engines dropped) once the browser
  // owns the gesture. Re-testing on scroll is what makes the state follow the grid.
  static #onTrackerScroll = () => {
    if (!ProductCard.#touchActive) {
      return;
    }
    ProductCard.#touchDragging = true;
    ProductCard.#scheduleDragPress();
  };

  static #onTrackerTouchEnd = () => {
    ProductCard.#touchActive = false;
    ProductCard.#touchDragging = false;
  };

  static #scheduleDragPress() {
    if (ProductCard.#hitTestFrame) {
      return;
    }
    ProductCard.#hitTestFrame = requestAnimationFrame(() => {
      ProductCard.#hitTestFrame = 0;
      ProductCard.#pressCardUnderFinger();
    });
  }

  static #pressCardUnderFinger() {
    if (!ProductCard.#touchActive || !ProductCard.#touchDragging) {
      return;
    }
    // Cheap bail before the hit test: only pocket cards have a pressed state at all.
    if (!responsive.matchesBreakpoint('pocket')) {
      return;
    }
    const target = document.elementFromPoint(ProductCard.#touchX, ProductCard.#touchY);
    const card = target?.closest?.('product-card');
    if (card instanceof ProductCard) {
      card.#pressFromDrag();
    }
  }

  static #bindTracker() {
    if (ProductCard.#trackerBound) {
      return;
    }
    ProductCard.#trackerBound = true;
    document.addEventListener('touchstart', ProductCard.#onTrackerTouchStart, { passive: true });
    document.addEventListener('touchmove', ProductCard.#onTrackerTouchMove, { passive: true });
    document.addEventListener('touchend', ProductCard.#onTrackerTouchEnd, { passive: true });
    document.addEventListener('touchcancel', ProductCard.#onTrackerTouchEnd, { passive: true });
    window.addEventListener('scroll', ProductCard.#onTrackerScroll, { passive: true });
  }

  #currentIndex = 0;
  #slideCount = 0;
  #images = [];
  #mediaEl = null;
  #sliderEl = null;
  #prevBtn = null;
  #nextBtn = null;
  #indicator = null;
  #sizesObservers = [];
  #onBreakpointChange = null;
  #sliderEnabled = false;
  #sliderAvailable = false;
  #sliderListenersBound = false;
  #insideSectionSwiper = false;
  #initialized = false;
  #dragPointerId = null;
  #dragStartX = 0;
  #dragStartY = 0;
  #dragAxis = null;
  #touchDragActive = false;
  #interactionDidSwipe = false;
  #pressTimer = null;
  #interactionDidPress = false;

  #onDocumentClick = (event) => {
    if (!this.contains(event.target)) {
      this.#clearPressed();
    }
  };

  #onCardClick = (event) => {
    if (this.#interactionDidSwipe) {
      this.#interactionDidSwipe = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // A hold that opened the pressed state also produces a synthetic click when the
    // finger lifts. Ignore that click so the state persists; the next deliberate tap
    // starts a new touch and is allowed to navigate normally.
    if (this.#interactionDidPress) {
      this.#interactionDidPress = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // One-size quick ATC — must run before PDP navigation.
    const addSizeButton = this.#findAddSizeButton(event);
    if (addSizeButton) {
      event.preventDefault();
      event.stopImmediatePropagation();
      void this.#addToCartFromButton(addSizeButton);
      return;
    }

    const interactive = event.target.closest(
      '.product-card__link, [data-action="pc-prev"], [data-action="pc-next"], [data-action="pc-color"], .product-card__colors a'
    );
    if (interactive) {
      return;
    }

    const url = this.dataset.url;
    if (url && url !== '#') {
      // A deliberate card click ends the persistent pressed state before navigation.
      this.#clearPressed();
      this.dispatchEvent(
        new CustomEvent('product-card:navigate', {
          bubbles: true,
          composed: true,
          detail: { url },
        })
      );
      // Instant feedback: the old page stays frozen on screen until the PDP is
      // ready (cross-document view transition), so acknowledge the tap now.
      // The timeout un-dims if the navigation is canceled (stop button, esc) —
      // in that case the page never unloads and pageshow never fires.
      this.classList.add(NAVIGATING_CLASS);
      window.setTimeout(() => this.classList.remove(NAVIGATING_CLASS), 8000);
      window.location.href = url;
    }
  };

  #findAddSizeButton(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    for (const node of path) {
      if (node instanceof Element && node.matches('[data-action="pc-add-size"]')) {
        return this.contains(node) ? node : null;
      }
    }
    const closest = event.target?.closest?.('[data-action="pc-add-size"]');
    return closest && this.contains(closest) ? closest : null;
  }

  #onAddSizeButton = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement) || !this.contains(button)) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    void this.#addToCartFromButton(button);
  };

  #addToCartFromButton(button) {
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') {
      return Promise.resolve();
    }

    const variantId = button.dataset.variantId;
    if (!variantId) {
      return Promise.resolve();
    }

    return this.#addToCart(variantId, button);
  }

  #onPointerEnter = () => {
    if (!responsive.matchesBreakpoint('lap_and_up')) {
      return;
    }
    // sliderAvailable, not sliderEnabled: small cards turn the interactive slider off
    // but must keep the desktop hover preview.
    if (!this.#sliderAvailable || this.#slideCount < 2) {
      return;
    }
    if (this.#currentIndex === 0) {
      this.#goToSlide(1, false, true);
    }
    this.#updateArrows();
    requestAnimationFrame(() => {
      this.#layoutAllSizes();
    });
  };

  #onPointerLeave = () => {
    if (!responsive.matchesBreakpoint('lap_and_up')) {
      return;
    }
    if (!this.#sliderAvailable) {
      return;
    }
    this.#goToSlide(0, false, true);
    requestAnimationFrame(() => {
      this.#layoutAllSizes();
    });
  };

  #onDragStart(clientX, clientY) {
    this.#dragStartX = clientX;
    this.#dragStartY = clientY;
    this.#dragAxis = null;
    this.#interactionDidSwipe = false;
  }

  #onDragMove(clientX, clientY, event) {
    if (!this.#sliderEnabled || this.#slideCount < 2) {
      return;
    }

    const deltaX = clientX - this.#dragStartX;
    const deltaY = clientY - this.#dragStartY;

    // Lock the gesture axis within the first few pixels and preventDefault
    // every horizontal move from then on. Waiting for the full swipe
    // threshold lets the browser start a vertical page scroll first, after
    // which touchmove is no longer cancelable and the swipe is lost.
    if (this.#dragAxis === null) {
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < AXIS_LOCK_PX) {
        return;
      }
      this.#dragAxis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
    }

    if (this.#dragAxis === 'y') {
      return;
    }

    event?.preventDefault?.();

    if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX) {
      return;
    }

    if (deltaX < 0 && this.#currentIndex < this.#slideCount - 1) {
      this.#goToSlide(this.#currentIndex + 1, true);
      this.#dragStartX = clientX;
      this.#interactionDidSwipe = true;
    } else if (deltaX > 0 && this.#currentIndex > 0) {
      this.#goToSlide(this.#currentIndex - 1, true);
      this.#dragStartX = clientX;
      this.#interactionDidSwipe = true;
    }
  }

  #onDragEnd() {
    this.#dragPointerId = null;
    this.#touchDragActive = false;
    this.#dragAxis = null;
  }

  #onSliderPointerDown = (event) => {
    if (!this.#sliderEnabled || this.#slideCount < 2) {
      return;
    }
    if (event.pointerType !== 'mouse' || event.button !== 0) {
      return;
    }
    if (event.target.closest('[data-action="pc-prev"], [data-action="pc-next"]')) {
      return;
    }

    this.#dragPointerId = event.pointerId;
    this.#onDragStart(event.clientX, event.clientY);
    this.#sliderEl?.setPointerCapture(event.pointerId);
  };

  #onSliderPointerMove = (event) => {
    if (event.pointerType !== 'mouse' || this.#dragPointerId !== event.pointerId) {
      return;
    }
    this.#onDragMove(event.clientX, event.clientY, event);
  };

  #onSliderPointerUp = (event) => {
    if (this.#dragPointerId !== event.pointerId) {
      return;
    }
    if (this.#sliderEl?.hasPointerCapture(event.pointerId)) {
      this.#sliderEl.releasePointerCapture(event.pointerId);
    }
    this.#onDragEnd();
  };

  #onTouchStart = (event) => {
    if (!this.#sliderEnabled || this.#slideCount < 2) {
      return;
    }
    if (event.target.closest('[data-action="pc-prev"], [data-action="pc-next"]')) {
      return;
    }

    const touch = event.touches[0];
    if (!touch) {
      return;
    }
    this.#touchDragActive = true;
    this.#onDragStart(touch.clientX, touch.clientY);
  };

  #onTouchMove = (event) => {
    if (!this.#touchDragActive || !this.#sliderEnabled || this.#slideCount < 2) {
      return;
    }

    const touch = event.touches[0];
    if (!touch) {
      return;
    }
    this.#onDragMove(touch.clientX, touch.clientY, event);
  };

  #onTouchEnd = () => {
    this.#onDragEnd();
  };

  #onPrev = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.#currentIndex > 0) {
      this.#goToSlide(this.#currentIndex - 1, true);
    }
  };

  #onNext = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.#currentIndex < this.#slideCount - 1) {
      this.#goToSlide(this.#currentIndex + 1, true);
    }
  };

  #onColorClick = (event) => {
    event.stopPropagation();
  };

  #bindSizeButtons() {
    this.querySelectorAll('[data-action="pc-add-size"]').forEach((button) => {
      button.addEventListener('click', this.#onAddSizeButton, true);
    });
  }

  #unbindSizeButtons() {
    this.querySelectorAll('[data-action="pc-add-size"]').forEach((button) => {
      button.removeEventListener('click', this.#onAddSizeButton, true);
    });
  }

  #shouldEnableSlider() {
    if (!this.#sliderAvailable) {
      return false;
    }
    // Small = the dense image wall (collection grid desktop 6-up / mobile 3-up). No arrows
    // and no swipe on any breakpoint; desktop keeps only the hover preview, which is gated
    // on #sliderAvailable rather than #sliderEnabled.
    if (this.classList.contains('product-card--small')) {
      return false;
    }
    // Nested in a section carousel: only disable image swipe on pocket so the
    // outer swiper can scroll. Desktop keeps hover arrows / image slider.
    if (this.#insideSectionSwiper && responsive.matchesBreakpoint('pocket')) {
      return false;
    }
    return true;
  }

  setCardSize(cardSize) {
    if (!['big', 'medium', 'small'].includes(cardSize)) {
      return;
    }

    this.classList.remove('product-card--big', 'product-card--medium', 'product-card--small');
    this.classList.add(`product-card--${cardSize}`);

    if (this.#initialized) {
      this.#clearPressed();
      this.#syncSliderEnabledState();
      requestAnimationFrame(() => this.#layoutAllSizes());
    }
  }

  #bindSliderListeners() {
    if (this.#sliderListenersBound || !this.#sliderEl) {
      return;
    }
    this.#prevBtn?.addEventListener('click', this.#onPrev);
    this.#nextBtn?.addEventListener('click', this.#onNext);
    this.#sliderEl.addEventListener('pointerdown', this.#onSliderPointerDown);
    this.#sliderEl.addEventListener('pointermove', this.#onSliderPointerMove);
    this.#sliderEl.addEventListener('pointerup', this.#onSliderPointerUp);
    this.#sliderEl.addEventListener('pointercancel', this.#onSliderPointerUp);
    this.#sliderEl.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.#sliderEl.addEventListener('touchmove', this.#onTouchMove, { passive: false });
    this.#sliderEl.addEventListener('touchend', this.#onTouchEnd);
    this.#sliderEl.addEventListener('touchcancel', this.#onTouchEnd);
    this.#sliderListenersBound = true;
  }

  #unbindSliderListeners() {
    if (!this.#sliderListenersBound) {
      return;
    }
    this.#prevBtn?.removeEventListener('click', this.#onPrev);
    this.#nextBtn?.removeEventListener('click', this.#onNext);
    this.#sliderEl?.removeEventListener('pointerdown', this.#onSliderPointerDown);
    this.#sliderEl?.removeEventListener('pointermove', this.#onSliderPointerMove);
    this.#sliderEl?.removeEventListener('pointerup', this.#onSliderPointerUp);
    this.#sliderEl?.removeEventListener('pointercancel', this.#onSliderPointerUp);
    this.#sliderEl?.removeEventListener('touchstart', this.#onTouchStart);
    this.#sliderEl?.removeEventListener('touchmove', this.#onTouchMove);
    this.#sliderEl?.removeEventListener('touchend', this.#onTouchEnd);
    this.#sliderEl?.removeEventListener('touchcancel', this.#onTouchEnd);
    this.#sliderListenersBound = false;
  }

  #syncSliderEnabledState() {
    const enable = this.#shouldEnableSlider();
    this.#sliderEnabled = enable;

    if (enable) {
      this.setAttribute('data-slider', 'true');
      this.#bindSliderListeners();
      this.#updateArrows();
      this.#updateIndicator();
    } else {
      this.removeAttribute('data-slider');
      this.#unbindSliderListeners();
      if (this.#currentIndex !== 0) {
        this.#goToSlide(0, false, true);
      }
    }
  }

  connectedCallback() {
    if (this.#initialized) {
      return;
    }
    this.#initialized = true;

    this.#insideSectionSwiper = Boolean(this.closest('swiper-init'));
    this.#sliderAvailable = this.hasAttribute('data-slider');
    this.#images = [...this.querySelectorAll('.product-card__image')];
    this.#slideCount = this.#images.length || parseInt(this.dataset.slideCount, 10) || 0;
    this.#mediaEl = this.querySelector('[data-ref="media"]');
    this.#sliderEl = this.querySelector('[data-ref="slider"]');
    this.#prevBtn = this.querySelector('[data-action="pc-prev"]');
    this.#nextBtn = this.querySelector('[data-action="pc-next"]');
    this.#indicator = this.querySelector('[data-ref="indicator"]');

    this.addEventListener('click', this.#onCardClick);
    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('touchstart', this.#onPrefetchIntent, { passive: true });
    this.addEventListener('pointerenter', this.#onPrefetchIntent);
    this.addEventListener('touchstart', this.#onCardTouchStart, { passive: true });
    this.addEventListener('touchend', this.#onCardTouchEnd);
    this.addEventListener('touchcancel', this.#onCardTouchEnd);

    this.#bindSizeButtons();
    this.querySelectorAll('[data-action="pc-color"]').forEach((el) => {
      el.addEventListener('click', this.#onColorClick);
    });

    this.#syncSliderEnabledState();

    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('product-card:pressed', this.#onOtherCardPressed);

    this.#initSizesLayout();

    this.#onBreakpointChange = () => {
      if (!responsive.matchesBreakpoint('pocket')) {
        this.#clearPressed();
      }
      this.#syncSliderEnabledState();
      this.#layoutAllSizes();
    };
    if (!ProductCard.#breakpointsRegistered) {
      responsive.addBreakpointListener('lap_and_up');
      responsive.addBreakpointListener('pocket');
      ProductCard.#breakpointsRegistered = true;
    }
    ProductCard.#bindTracker();
    document.body.addEventListener('breakpoint:lap_and_up', this.#onBreakpointChange);
    document.body.addEventListener('breakpoint:pocket', this.#onBreakpointChange);

    document.fonts?.ready.then(() => {
      this.#layoutAllSizes();
    });
  }

  disconnectedCallback() {
    this.#sizesObservers.forEach((observer) => observer.disconnect());
    this.#sizesObservers = [];
    document.body.removeEventListener('breakpoint:lap_and_up', this.#onBreakpointChange);
    document.body.removeEventListener('breakpoint:pocket', this.#onBreakpointChange);
    this.removeEventListener('click', this.#onCardClick);
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    this.removeEventListener('touchstart', this.#onPrefetchIntent);
    this.removeEventListener('pointerenter', this.#onPrefetchIntent);
    this.#unbindSizeButtons();
    this.#unbindSliderListeners();
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('product-card:pressed', this.#onOtherCardPressed);
    this.#clearPressTimer();
    this.removeEventListener('touchstart', this.#onCardTouchStart);
    this.removeEventListener('touchend', this.#onCardTouchEnd);
    this.removeEventListener('touchcancel', this.#onCardTouchEnd);
    this.#mediaEl = null;
    this.#sliderEl = null;
    this.#initialized = false;
  }

  #onOtherCardPressed = (event) => {
    if (event.detail.card !== this) {
      this.#clearPressed();
    }
  };

  #onPrefetchIntent = () => {
    prefetchDocument(this.dataset.url);
  };

  // Pressed comes from a finger resting on the card. Dragging over it is handled by the
  // page-level tracker instead, since the drag belongs to whatever card it started on.
  #onCardTouchStart = (event) => {
    if (!this.#isMediumMobile()) {
      return;
    }

    // These run their own actions; a hold on them must not press the card.
    if (
      event.target.closest(
        '[data-action="pc-add-size"], [data-action="pc-prev"], [data-action="pc-next"], [data-action="pc-color"], .product-card__colors a'
      )
    ) {
      return;
    }

    // A hold during a scroll normally produces no click. Reset this per gesture so its
    // stale flag cannot swallow the next deliberate tap on the card.
    this.#interactionDidPress = false;
    this.#clearPressTimer();
    this.#pressTimer = window.setTimeout(() => {
      this.#pressTimer = null;
      // The finger moved on within the hold window: the gesture is a drag now, and the
      // tracker decides which card is under it. Without this the timer would press the
      // card the finger merely started on, stealing the state from the one it is over.
      if (ProductCard.#touchDragging || this.classList.contains(PRESSED_CLASS)) {
        return;
      }
      this.#setPressed();
      this.#interactionDidPress = true;
    }, PRESS_HOLD_MS);
  };

  #onCardTouchEnd = () => {
    this.#clearPressTimer();
  };

  // Called by the document tracker for whichever card the finger is currently over.
  #pressFromDrag() {
    if (!this.#isMediumMobile() || this.classList.contains(PRESSED_CLASS)) {
      return;
    }
    // The finger is moving, so the hold it started is no longer what pressed this card.
    this.#clearPressTimer();
    // Drag/scroll gestures do not reliably synthesize a click, so do not arm
    // #interactionDidPress here; doing so could swallow the next real tap.
    this.#setPressed();
  }

  #clearPressTimer() {
    if (this.#pressTimer != null) {
      window.clearTimeout(this.#pressTimer);
      this.#pressTimer = null;
    }
  }

  #isMediumMobile() {
    // Cards nested in a section carousel never press — slider sections use
    // 'big' cards per Figma, and big cards have no pressed state.
    // data-card-press-disabled (collection grid with the 2-column sizes row
    // turned off): a hold would reveal nothing, so there is nothing to press.
    return (
      this.classList.contains('product-card--medium') &&
      responsive.matchesBreakpoint('pocket') &&
      !this.#insideSectionSwiper &&
      !this.closest('[data-card-press-disabled]')
    );
  }

  #setPressed() {
    document.dispatchEvent(new CustomEvent('product-card:pressed', { detail: { card: this } }));
    this.classList.add(PRESSED_CLASS);
  }

  #clearPressed() {
    this.classList.remove(PRESSED_CLASS);
  }

  #goToSlide(index, keepPressed, instant = false) {
    if (index < 0 || index >= this.#slideCount) {
      return;
    }
    this.#currentIndex = index;

    if (instant) {
      const thumb = this.#indicator?.querySelector('.product-card__indicator-thumb');
      this.#images.forEach((img) => {
        img.style.transition = 'none';
      });
      if (thumb) {
        thumb.style.transition = 'none';
      }
    }

    this.#images.forEach((img, i) => {
      img.classList.toggle(ACTIVE_CLASS, i === index);
    });

    if (instant) {
      const thumb = this.#indicator?.querySelector('.product-card__indicator-thumb');
      void this.#images[0]?.offsetWidth;
      this.#images.forEach((img) => {
        img.style.removeProperty('transition');
      });
      if (thumb) {
        thumb.style.removeProperty('transition');
      }
    }

    this.#updateArrows();
    if (keepPressed && this.#isMediumMobile()) {
      this.#setPressed();
    }
    this.#updateIndicator();
  }

  #updateIndicator() {
    if (!this.#indicator) {
      return;
    }
    this.#indicator.style.setProperty('--slide-count', String(this.#slideCount));
    this.#indicator.style.setProperty('--slide-index', String(this.#currentIndex));
  }

  #initSizesLayout() {
    const containers = this.querySelectorAll('[data-action="pc-sizes"]');
    containers.forEach((container) => {
      const runLayout = () => {
        this.#layoutSizes(container);
      };
      const observer = new ResizeObserver(runLayout);
      observer.observe(container);
      if (container.parentElement) {
        observer.observe(container.parentElement);
      }
      this.#sizesObservers.push(observer);
    });

    requestAnimationFrame(() => {
      this.#layoutAllSizes();
    });
  }

  #layoutAllSizes() {
    this.querySelectorAll('[data-action="pc-sizes"]').forEach((container) => {
      this.#layoutSizes(container);
    });
  }

  #getSizesAvailableWidth(container) {
    if (container.clientWidth > 0) {
      return container.clientWidth;
    }
    return container.parentElement?.clientWidth ?? 0;
  }

  #measureSizesContentWidth(container) {
    const style = getComputedStyle(container);
    const gap = parseFloat(style.columnGap) || parseFloat(style.gap) || 0;
    const children = [...container.children].filter((child) => !child.hidden);

    return children.reduce((total, child, index) => {
      const childWidth = child.getBoundingClientRect().width;
      return total + childWidth + (index > 0 ? gap : 0);
    }, 0);
  }

  #layoutSizes(container) {
    const sizes = [
      ...container.querySelectorAll('.product-card__size:not(.product-card__size--one)'),
    ];
    const overflow = container.querySelector('.product-card__sizes-overflow');
    if (sizes.length === 0) {
      return;
    }

    const availableWidth = this.#getSizesAvailableWidth(container);
    if (availableWidth === 0) {
      return;
    }

    const maxVisible = parseInt(container.dataset.maxVisible, 10);
    const cap = Number.isNaN(maxVisible)
      ? sizes.length
      : Math.min(sizes.length, Math.max(1, maxVisible));

    const applyVisibleCount = (visibleCount) => {
      const remaining = sizes.length - visibleCount;
      sizes.forEach((size, index) => {
        size.hidden = index >= visibleCount;
      });
      if (!overflow) {
        return;
      }
      if (remaining > 0) {
        overflow.hidden = false;
        overflow.textContent = `+${remaining}`;
        overflow.setAttribute('aria-hidden', 'false');
      } else {
        overflow.hidden = true;
        overflow.textContent = '';
        overflow.setAttribute('aria-hidden', 'true');
      }
    };

    const fits = (visibleCount) => {
      applyVisibleCount(visibleCount);
      return this.#measureSizesContentWidth(container) <= availableWidth + 0.5;
    };

    if (fits(cap)) {
      return;
    }

    let low = 1;
    let high = cap - 1;
    let best = 1;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (fits(mid)) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    applyVisibleCount(best);
  }

  #updateArrows() {
    if (!this.#prevBtn || !this.#nextBtn) {
      return;
    }

    const atStart = this.#currentIndex === 0;
    const atEnd = this.#currentIndex >= this.#slideCount - 1;

    this.#prevBtn.toggleAttribute('disabled', atStart);
    this.#prevBtn.classList.toggle(DISABLED_CLASS, atStart);
    this.#prevBtn.setAttribute('aria-disabled', String(atStart));

    this.#nextBtn.toggleAttribute('disabled', atEnd);
    this.#nextBtn.classList.toggle(DISABLED_CLASS, atEnd);
    this.#nextBtn.setAttribute('aria-disabled', String(atEnd));
  }

  async #addToCart(variantId, button) {
    button.disabled = true;
    const pageLoader = document.body.querySelector('.page-loader');
    if (pageLoader) {
      pageLoader.style.width = '100%';
    }

    try {
      await addVariantToCart(variantId, 1, { dispatchTarget: this });
    } catch {
      /* silent — cart drawer / network errors shouldn't navigate away */
    } finally {
      button.disabled = false;
      if (pageLoader) {
        pageLoader.style.width = '0%';
      }
    }
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

// Collection pages restore from BFCache without a reload; un-dim the card the
// shopper navigated away from.
window.addEventListener('pageshow', () => {
  document.querySelectorAll(`.${NAVIGATING_CLASS}`).forEach((card) => {
    card.classList.remove(NAVIGATING_CLASS);
  });
});
